import { XMarkIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import type { Package } from '../../types/subscription';

interface PlanComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPackageId: string;
  packages: Package[];
  isChanging: boolean;
  onSelectPlan: (pkg: Package) => void;
  /** The customer is on the token ledger: monthly allowances, so say what a change does to them. */
  onLedger?: boolean;
}

export function PlanComparisonModal({
  isOpen,
  onClose,
  currentPackageId,
  packages,
  isChanging,
  onSelectPlan,
  onLedger = false,
}: PlanComparisonModalProps) {
  if (!isOpen) return null;

  const visiblePackages = packages
    .filter((p) => p.visibility === 'active')
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-4xl bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-slate-800/50">
          <div>
            <h2 className="text-xl font-bold text-white">
              Change Subscription Plan
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Select a new plan. Changes will be prorated automatically.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visiblePackages.map((pkg) => {
              const isCurrent = pkg.id === currentPackageId;
              const quarterlyPrice = pkg.monthly_price_cents * 3;

              return (
                <div
                  key={pkg.id}
                  className={clsx(
                    'relative flex flex-col p-6 rounded-xl border transition-all',
                    isCurrent
                      ? 'bg-slate-800 border-slate-600'
                      : pkg.is_popular
                        ? 'bg-slate-800/80 border-cyan-500/50 shadow-[0_0_30px_-10px_rgba(6,182,212,0.3)]'
                        : 'bg-slate-900/50 border-slate-700 hover:border-slate-600',
                  )}
                >
                  {pkg.is_popular && !isCurrent && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-cyan-500 text-slate-900 text-sm font-bold rounded-full uppercase tracking-wider">
                      Recommended
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-slate-600 text-white text-sm font-bold rounded-full uppercase tracking-wider">
                      Current Plan
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-lg font-bold text-white mb-2">
                      {pkg.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-bold text-white">
                        ${(quarterlyPrice / 100).toFixed(0)}
                      </span>
                      <span className="text-sm text-slate-400">NZD/qtr</span>
                    </div>
                    <p className="text-sm text-cyan-400 font-medium mt-2">
                      {(pkg.included_tokens * 3).toLocaleString()} tokens / quarter
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {(pkg.features || []).map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-sm text-slate-300"
                      >
                        <CheckIcon className="w-5 h-5 text-cyan-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    disabled={isCurrent || isChanging}
                    onClick={() => onSelectPlan(pkg)}
                    className={clsx(
                      'w-full py-2.5 px-4 rounded-lg font-medium transition-colors',
                      isCurrent
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                        : pkg.is_popular
                          ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900'
                          : 'bg-slate-700 hover:bg-slate-600 text-white',
                      isChanging && 'opacity-50 cursor-wait',
                    )}
                  >
                    {isCurrent ? 'Current Plan' : isChanging ? 'Processing...' : 'Select Plan'}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3">
            <ExclamationCircleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-400/90">
              <p className="font-medium text-amber-500 mb-1">
                Proration Notice
              </p>
              <p>
                When upgrading, you will be charged the prorated difference
                immediately. When downgrading, you will receive a credit on your
                next invoice.{' '}
                {onLedger
                  ? "An upgrade adds the new plan's tokens for this month straight away; a downgrade changes your tokens from next month."
                  : 'Token limits will be updated instantly.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
